import { useEffect, useMemo } from "react";
import * as THREE from "three";
import type { QrStandSpec } from "../scenario/types";

const DEG = Math.PI / 180;

/**
 * A small vertical "table-tent" standee with a QR code card (baked PNG —
 * QR matrix + caption — served like any other CDN texture) mounted on a
 * two-leg easel base. Purely decorative for now; a future beat can add a
 * click/zoom hotspot on it for a scan-the-QR mini-game.
 */
export function QrStand({ spec }: { spec: QrStandSpec }) {
  const scale = spec.scale ?? 1;
  const yaw = (spec.rotationDeg ?? 0) * DEG;

  const tex = useMemo(() => {
    const t = new THREE.TextureLoader().load(spec.qrImageUrl);
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 8;
    return t;
  }, [spec.qrImageUrl]);
  useEffect(() => () => tex.dispose(), [tex]);

  // Card ~11cm wide x 15cm tall (a compact "scan me" table-tent), tilted
  // back a few degrees off vertical so it reads naturally to an approaching
  // learner instead of standing perfectly (and un-realistically) upright.
  const CARD_W = 0.11;
  const CARD_H = 0.15;
  const TILT_DEG = 8;

  return (
    <group position={spec.position} rotation={[0, yaw, 0]} scale={scale}>
      {/* easel base plate */}
      <mesh position={[0, 0.004, 0]}>
        <boxGeometry args={[0.09, 0.008, 0.07]} />
        <meshStandardMaterial color="#e9e6df" metalness={0.05} roughness={0.7} />
      </mesh>
      {/* two support legs from the base up to the card's back */}
      {[-0.035, 0.035].map((x, i) => (
        <mesh key={i} position={[x, CARD_H / 2 + 0.01, -0.012]} rotation={[TILT_DEG * DEG, 0, 0]}>
          <boxGeometry args={[0.006, CARD_H * 0.9, 0.006]} />
          <meshStandardMaterial color="#cfd4d9" metalness={0.4} roughness={0.4} />
        </mesh>
      ))}
      {/* the card itself */}
      <group position={[0, CARD_H / 2 + 0.014, 0]} rotation={[TILT_DEG * DEG, 0, 0]}>
        <mesh>
          <boxGeometry args={[CARD_W, CARD_H, 0.004]} />
          <meshStandardMaterial color="#ffffff" roughness={0.85} />
        </mesh>
        <mesh position={[0, 0, 0.0025]}>
          <planeGeometry args={[CARD_W * 0.94, CARD_H * 0.94]} />
          <meshStandardMaterial map={tex} roughness={0.9} />
        </mesh>
        {/* back face mirrors the same QR so it reads from either side */}
        <mesh position={[0, 0, -0.0025]} rotation={[0, Math.PI, 0]}>
          <planeGeometry args={[CARD_W * 0.94, CARD_H * 0.94]} />
          <meshStandardMaterial map={tex} roughness={0.9} />
        </mesh>
      </group>
    </group>
  );
}
